import React, { memo, useEffect, useState, useRef, useMemo } from "react";
import { TextField, Autocomplete, Box, Chip } from "@mui/material";
import debounce from "lodash.debounce";
import { InputAdornment } from '@mui/material';

const useDebounce = (callback) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current?.();
    };

    return debounce(func, 1000);
  }, []);

  return debouncedCallback;
};

const ScrollableMultiSelect = ({
  title,
  control,
  setControl,
  disabled = false,
  initialData = [],
  loadData = () => { },
}) => {
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [isFullDataLoad, setIsFullDataLoad] = useState(false);
  const [searchInput, setSearchInput] = useState("")
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsFullDataLoad(false);
    setPage(1);
    setOptions(initialData);
    setSearchInput("");
  }, [initialData]);

  const handleChange = (event, newValue) => {
    setControl(newValue);
  };

  const loadOnSearch = async (val) => {
    let items = []
    setLoading(true);
    const response = await loadData(1, searchInput);
    if (response?.length === 0) {
      setIsFullDataLoad(true)
    } else {
      setPage(1);
      items = response || []
      setOptions([...items]);
    }
    setLoading(false);
  }

  const debouncedOnChange = useDebounce(loadOnSearch);

  const handleInputChange = (event, newInputValue) => {
    debouncedOnChange();
    setSearchInput(newInputValue);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const loadMoreResults = async () => {
    const nextPage = page + 1;
    let items = []
    setLoading(true);

    const response = await loadData(nextPage, searchInput);
    if (response?.length === 0) {
      setIsFullDataLoad(true)
    } else {
      setPage(nextPage);
      items = response || []
      setOptions([...options, ...items]);
    }
    setLoading(false);
  };

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;

    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1 && !isFullDataLoad) {
      loadMoreResults();
    }
  };

  return (
    <>
      <Box
        component="legend"
        sx={{ typography: "subtitle1", mb: 1, fontSize: 14 }}
        style={{
          marginBottom: "10px !important",
          textAlign: "left",
          font: "normal normal 550 13px/18px Open Sans",
          color: "black"
        }}
      >
        {title}
      </Box>
      <Autocomplete
        multiple
        value={control || ""}
        onChange={handleChange}
        options={options}
        autoHighlight
        getOptionLabel={(option) => option}
        onInputChange={handleInputChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        id="type-select"
        size="small"
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password", // disable autocomplete and autofill
            }}
            InputProps={{
              ...params.InputProps,
              ...(loading && {
                endAdornment:
                  <InputAdornment position="end">
                    <div
                      style={{
                        position: "absolute",
                        right: "14px",
                        display: "flex",
                        alignItems: "center",
                        width: "20px",
                        height: "20px"
                      }}
                    >
                      <span className="circle-loader"></span>
                    </div>
                  </InputAdornment>
              })
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
          ))
        }
        ListboxProps={{
          onScroll: handleScroll,
        }}
      />
    </>
  );
};

export default memo(ScrollableMultiSelect);
